import { Zap, Tag, FileText, Settings, Coins, ArrowRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const features = [
  {
    icon: Zap,
    title: "AI Extraction",
    pain: "CRMs make you set up fields, workflows, and integrations.",
    solution: "We extract contact data instantly from any email. Zero setup.",
    emoji: "⚡",
    bgColor: "bg-brand-orange/10",
    hoverBg: "group-hover:bg-brand-orange/20",
    textColor: "text-brand-orange",
    gradient: "from-brand-orange/20 to-brand-orange/5",
  },
  {
    icon: Tag,
    title: "Smart Categories",
    pain: "CRM tagging systems force you into their generic schemas.",
    solution: "Our AI learns YOUR categories and assigns color tags automatically.",
    emoji: "🏷️",
    bgColor: "bg-brand-cyan/10",
    hoverBg: "group-hover:bg-brand-cyan/20",
    textColor: "text-brand-cyan",
    gradient: "from-brand-cyan/20 to-brand-cyan/5",
  },
  {
    icon: FileText,
    title: "Smart Notes",
    pain: "Researching people or companies means opening 10 browser tabs.",
    solution: "Get web + LinkedIn summaries in seconds — right inside Outlook.",
    emoji: "📝",
    bgColor: "bg-brand-pink/10",
    hoverBg: "group-hover:bg-brand-pink/20",
    textColor: "text-brand-pink",
    gradient: "from-brand-pink/20 to-brand-pink/5",
  },
  {
    icon: Settings,
    title: "Field Presets",
    pain: "CRMs store every field imaginable. Most are useless.",
    solution: "Extract only what you need. Keep your contact data focused and clean.",
    emoji: "⚙️",
    bgColor: "bg-brand-blue/10",
    hoverBg: "group-hover:bg-brand-blue/20",
    textColor: "text-brand-blue",
    gradient: "from-brand-blue/20 to-brand-blue/5",
  },
  {
    icon: Coins,
    title: "Adaptive Credits",
    pain: "CRM subscriptions charge you whether you use them or not.",
    solution: "Pay only for AI features you actually use. Transparent and scalable.",
    emoji: "💰",
    bgColor: "bg-brand-orange/10",
    hoverBg: "group-hover:bg-brand-orange/20",
    textColor: "text-brand-orange",
    gradient: "from-brand-orange/20 to-brand-orange/5",
  },
];

export const Features = () => {
  const [visibleCards, setVisibleCards] = useState<boolean[]>(new Array(features.length).fill(false));
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = cardRefs.current.map((card, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const newVisible = [...prev];
                  newVisible[index] = true;
                  return newVisible;
                });
              }, index * 100);
            }
          });
        },
        { threshold: 0.1 }
      );
      
      if (card) observer.observe(card);
      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (cardRefs.current[index]) {
          observer.unobserve(cardRefs.current[index]!);
        }
      });
    };
  }, []);

  return (
    <section id="features" className="py-32 bg-background relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-pink/10 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-cyan/10 rounded-full blur-[120px] animate-blob animation-delay-4000" />
      </div>

      <div className="container px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-block px-4 py-2 mb-6 glass-card rounded-full">
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              Powerful Features
            </span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
            Simple tools.{" "}
            <span className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift">
              Powerful results.
            </span>
          </h2>
          
          <p className="text-xl text-muted-foreground animate-fade-in-up animation-delay-200">
            You don't need a full CRM. You need these five features — and nothing else.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`group relative transition-all duration-700 ${
                visibleCards[index] 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Gradient Border Effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradient} rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              {/* Card Content */}
              <div className="relative glass-card-strong rounded-2xl p-8 h-full hover-lift border border-border/50 group-hover:border-border">
                {/* Icon with Floating Animation */}
                <div className={`mb-6 inline-flex p-4 rounded-xl ${feature.bgColor} ${feature.hoverBg} transition-smooth animate-float`}>
                  <span className="text-4xl filter drop-shadow-lg">{feature.emoji}</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                
                <div className="space-y-5">
                  {/* Pain Point */}
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/5 border border-destructive/10">
                    <X className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.pain}
                    </p>
                  </div>
                  
                  {/* Solution */}
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <ArrowRight className={`w-5 h-5 ${feature.textColor} flex-shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform`} />
                    <p className="text-sm text-foreground leading-relaxed font-medium">
                      {feature.solution}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
